import { fs } from '@/theme/tokens';
import { Alert, Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { fleetStorageUsage, platformStorageAdmin, type FleetRow } from '@/api/storage-admin';
import { formatBytes } from '@/components/fields';
import { LocalDataTable } from '@/components/grid/LocalDataTable';
import { HealthChip, serverError } from '@/pages/storage/common';
import { StorageAdminPanel } from '@/pages/storage/StorageAdminPanel';
import type { PlatformCall } from './common';

interface Workspace {
  tenant_key: string;
  display_name: string;
  instance_url: string;
}

function originOf(url: string): string {
  try {
    return new URL(url).origin;
  } catch {
    return url;
  }
}

/** Every workspace's storage at a glance; pick one to change where its files go and what it may hold. */
export function StorageTab({ call, tenants }: { call: PlatformCall; tenants: Workspace[] }) {
  const fleet = useQuery({ queryKey: ['storage-admin', 'fleet'], queryFn: () => fleetStorageUsage(call) });
  const [selected, setSelected] = useState<string | null>(null);
  const admin = useMemo(() => (selected ? platformStorageAdmin(call, selected) : null), [call, selected]);
  const workspace = tenants.find((t) => t.tenant_key === selected);
  const origins = useMemo(() => (workspace?.instance_url ? [originOf(workspace.instance_url)] : []), [workspace]);
  const nameOf = (key: string) => tenants.find((t) => t.tenant_key === key)?.display_name ?? key;

  const columns: GridColDef<FleetRow>[] = [
    { field: 'tenant_key', headerName: 'Workspace', minWidth: 200, flex: 1, valueGetter: (_v, r) => nameOf(r.tenant_key) },
    { field: 'mode', headerName: 'Stored on', minWidth: 110, valueFormatter: (v: string) => (v === 'S3' ? 'S3' : 'Server') },
    {
      field: 'used_bytes', headerName: 'Used', type: 'number', minWidth: 150,
      valueGetter: (_v, r) => r.used_bytes,
      renderCell: ({ row }) => `${formatBytes(row.used_bytes)}${row.quota_ceiling_bytes ? ` of ${formatBytes(row.quota_ceiling_bytes)}` : ''}`,
    },
    { field: 'file_count', headerName: 'Files', type: 'number', minWidth: 90 },
    { field: 'pending', headerName: 'Waiting', type: 'number', minWidth: 90, valueGetter: (_v, r) => r.queue.pending_sync },
    {
      field: 'failed', headerName: 'Failed', type: 'number', minWidth: 90, valueGetter: (_v, r) => r.queue.failed,
      cellClassName: (p) => (p.value ? 'strong' : ''),
    },
    {
      field: 'profiles', headerName: 'S3 servers', minWidth: 220, flex: 1, sortable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ height: '100%' }}>
          {row.profiles.length
            ? row.profiles.map((p) => <Chip key={p.name} size="small" variant="outlined" label={p.name} />)
            : '—'}
          {row.profiles.some((p) => p.health === 'FAIL') && <HealthChip status="FAIL" />}
        </Stack>
      ),
    },
  ];

  return (
    <Stack spacing={2}>
      {fleet.error && <Alert severity="error">{serverError(fleet.error).message}</Alert>}
      <LocalDataTable
        rows={fleet.data ?? []} columns={columns} getRowId={(r) => r.tenant_key} loading={fleet.isLoading}
        searchText={(r) => `${r.tenant_key} ${nameOf(r.tenant_key)}`} searchPlaceholder="Search workspaces"
        noun={['workspace', 'workspaces']} onRowClick={(r) => setSelected(r.tenant_key)} selectedId={selected}
        summary={(rows) => [
          ['Stored', formatBytes(rows.reduce((n, r) => n + r.used_bytes, 0))],
          ['Files', rows.reduce((n, r) => n + r.file_count, 0).toLocaleString('en-IN')],
          ['Failed to reach S3', String(rows.reduce((n, r) => n + r.queue.failed, 0))],
        ]}
        toolbarEnd={<Button size="small" onClick={() => fleet.refetch()}>Refresh</Button>}
        emptyMessage="No active workspaces."
      />
      {admin && selected ? (
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>{nameOf(selected)}</Typography>
          <StorageAdminPanel key={selected} admin={admin} corsOrigins={origins} />
        </Paper>
      ) : (
        <Box sx={{ color: 'text.secondary', fontSize: fs.base }}>Pick a workspace to change its storage.</Box>
      )}
    </Stack>
  );
}
